import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { fetchWallsForMatter } from '@/lib/ethicalWallsApi';

/**
 * Information barriers on one matter — for the chambers administrator.
 *
 * GET /ethical-walls/for-matter/:matterId ("used by the matter page to show its wall state") and
 * its client existed with no caller. A wall links two opposing matters and blocks the clerks who
 * could reach both; admins sit outside it. The panel names the OPPOSING matter, which is exactly
 * what the wall protects, so it is rendered for admins only. Walls are erected and lifted on the
 * Information Barriers page, which this links to — no second place to change them.
 */
export function MatterInformationBarriersPanel({ matterId }: { matterId: string }) {
  const q = useQuery({
    queryKey: ['ethical-walls', 'for-matter', matterId],
    queryFn: () => fetchWallsForMatter(matterId),
    staleTime: 30_000,
  });

  return (
    <Card data-testid="information-barriers">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-[15px]">
          <ShieldCheck className="h-4 w-4 text-brand-600 dark:text-brand-400" weight="duotone" />
          Information barriers
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {q.isLoading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : q.isError ? (
          <QueryErrorState compact what="information barriers" onRetry={() => void q.refetch()} />
        ) : (q.data ?? []).length === 0 ? (
          <p className="text-xs text-muted-foreground">No information barrier on this matter.</p>
        ) : (
          <ul className="space-y-2">
            {(q.data ?? []).map((w) => {
              const other = w.matterA.id === matterId ? w.matterB : w.matterA;
              return (
                <li key={w.id} data-testid="information-barrier" className="rounded-md border border-border px-3 py-2 text-xs">
                  <div className="font-medium text-foreground">
                    Walled from {other.caseNumber} — {other.caseName}
                  </div>
                  {w.reason && <div className="mt-0.5 text-muted-foreground">{w.reason}</div>}
                  <div className="mt-0.5 text-muted-foreground">
                    Since {new Date(w.erectedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <Link to="/admin/ethical-walls" className="inline-block text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">
          Manage information barriers
        </Link>
      </CardContent>
    </Card>
  );
}
